import React, { useEffect, useRef, useState } from 'react';
import InputColor, { InputColorProps } from 'react-input-color';
import { useDispatch, useSelector } from 'react-redux';
import { tryToUploadChatFiles } from '../../../store/brokers/partners/chats/ChatsSlice';
import {
    fetchBrandingConfig,
    patchBrandingConfig,
} from '../../../store/brokers/partners/branding/BrandingSlice';
import { RootState } from '../../../app/store';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../../../theme-context';
import Cookies from 'js-cookie';

type BrandingConfig = {
    signInImageRHS: string;
    chatMainImage: string;
    chatOverlayLogo: string;
    chatLogoTopLeft: string;
    adminSignInImage: string;
    userSignInImage: string;
    searchBarBorderColor: string;
    ctaButtonColor: string;
    dialogueBoxHeaderColor: string;
    menuTabLabel: string;
};

type BrandingConfigCardProps = {
    hotelUsername: string;
    type: string;
};

const IMAGE_FIELDS: { name: keyof BrandingConfig; label: string }[] = [
    { name: 'signInImageRHS', label: 'Sign-In Image (RHS)' },
    { name: 'chatMainImage', label: 'Chat Main Image' },
    { name: 'chatOverlayLogo', label: 'Overlay Logo on Main Image' },
    { name: 'chatLogoTopLeft', label: 'Logo Top Left (Chat)' },
    { name: 'adminSignInImage', label: 'Admin Sign-In Image' },
    { name: 'userSignInImage', label: 'User Sign-In Image' },
];

const COLOR_FIELDS: {
    name: keyof BrandingConfig;
    label: string;
    default: string;
}[] = [
    {
        name: 'searchBarBorderColor',
        label: 'Search Bar Border Color',
        default: '#00330F',
    },
    { name: 'ctaButtonColor', label: 'CTA Button Color', default: '#00330F' },
    {
        name: 'dialogueBoxHeaderColor',
        label: 'Dialogue Box Header Color',
        default: '#00330F',
    },
];

function shallowEqual(a: BrandingConfig, b: BrandingConfig) {
    for (const key in a) {
        if (a[key as keyof BrandingConfig] !== b[key as keyof BrandingConfig]) {
            return false;
        }
    }
    return true;
}

const BrandingConfigCard: React.FC<BrandingConfigCardProps> = ({
    hotelUsername,
    type,
}) => {
    const [username, setUsername] = useState('');
    const dispatch = useDispatch();

    const accessToken = Cookies.get('accessToken') || localStorage.getItem('accessToken');

    const { theme } = useTheme();
    const navigate = useNavigate();
    const onClickGoBack = () => {
        navigate('/insights');
    };

    useEffect(() => {
        if (accessToken) {
            // Split JWT into parts and get the payload
            const payloadBase64 = accessToken.split('.')[1];
            // Add padding if needed and replace URL-safe chars
            const base64 = payloadBase64.replace(/-/g, '+').replace(/_/g, '/');
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split('')
                    .map(function (c) {
                        return (
                            '%' +
                            ('00' + c.charCodeAt(0).toString(16)).slice(-2)
                        );
                    })
                    .join(''),
            );
            const payloadObj = JSON.parse(jsonPayload);
            setUsername(payloadObj['cognito:username']);
        }
    }, []);

    // Select Redux state
    const {
        isLoading,
        isSaving,
        config: reduxConfig,
        originalConfig,
    } = useSelector((state: RootState) => state.branding);

    const [config, setConfig] = useState<BrandingConfig>({
        signInImageRHS: '',
        chatMainImage: '',
        chatOverlayLogo: '',
        chatLogoTopLeft: '',
        adminSignInImage: '',
        userSignInImage: '',
        searchBarBorderColor: '#00330F',
        ctaButtonColor: '#00330F',
        dialogueBoxHeaderColor: '#00330F',
        menuTabLabel: '',
    });

    // Update local state when reduxConfig changes
    useEffect(() => {
        if (reduxConfig) {
            setConfig(reduxConfig);
        }
    }, [reduxConfig]);

    // Fetch from Redux on mount/param change
    useEffect(() => {
        // @ts-ignore
        if (username) {
            dispatch(
                fetchBrandingConfig({ hotelUsername: username, type }) as any,
            );
        }
        // eslint-disable-next-line
    }, [username, type]);

    const fileRefs = useRef<Record<string, HTMLInputElement | null>>({});

    // Input field change
    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setConfig((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Color change
    const handleColorChange = (field: keyof BrandingConfig) => (color: any) => {
        setConfig((prev) => ({
            ...prev,
            [field]: color.hex,
        }));
    };

    // Image upload
    const [uploading, setUploading] = useState(false);
    const handleFileUpload = async (
        e: React.ChangeEvent<HTMLInputElement>,
        field: keyof BrandingConfig,
    ) => {
        const file = e.target.files?.[0];
        if (!file) return;
        setUploading(true);
        try {
            const formData = new FormData();
            formData.append('files', file);
            const response = await dispatch(
                tryToUploadChatFiles(formData) as any,
            ).unwrap();
            const url = Array.isArray(response)
                ? response[0]?.url
                : response.url;
            setConfig((prev) => ({
                ...prev,
                [field]: url,
            }));
        } catch (err) {
            console.error('File upload failed:', err);
        }
        setUploading(false);
    };

    // Save config to backend (via Redux thunk)
    const handleSave = (e: React.FormEvent) => {
        e.preventDefault();
        dispatch(
            patchBrandingConfig({
                // @ts-ignore
                hotelUsername: username,
                type,
                data: config,
            }) as any,
        );
    };

    const isDirty = !!originalConfig && !shallowEqual(config, originalConfig);

    if (isLoading) {
        return (
            <div className="flex justify-center items-center py-16">
                <div className="text-gray-400 text-lg">Loading...</div>
            </div>
        );
    }

    return (
        <form
            onSubmit={handleSave}
            className="w-full mx-auto space-y-4">
            <div
                className="flex flex-row items-center cursor-pointer hover:opacity-80 mt-3 mb-8"
                onClick={onClickGoBack}>
                <img
                    src={
                        theme === 'dark'
                            ? '/assets/shared/arrow-back-white.svg'
                            : '/assets/shared/arrow-back.svg'
                    }
                    className={'object-contain w-[20px] mr-2'}
                />
                <p className="dark:text-primary-dark">UI Branding</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                {/* Image Inputs */}
                {IMAGE_FIELDS.map((field) => (
                    <div key={field.name}>
                        <label className="block text-gray-700 font-medium mb-1">
                            {field.label}
                        </label>
                        <div className="flex items-center space-x-2">
                            <div
                                className="h-10 w-10 border border-gray-200 rounded bg-white cursor-pointer hover:bg-gray-100 transition flex justify-center items-center"
                                onClick={() =>
                                    fileRefs.current[field.name]?.click()
                                }>
                                <img
                                    src="/assets/chat/file-attach.svg"
                                    className="object-contain w-[30px]"
                                    alt="Attach"></img>
                            </div>
                            <input
                                ref={(el) =>
                                    (fileRefs.current[field.name] = el)
                                }
                                type="file"
                                accept="image/*"
                                className="hidden"
                                onChange={(e) =>
                                    handleFileUpload(e, field.name)
                                }
                                disabled={uploading}
                            />
                            <input
                                type="text"
                                name={field.name}
                                value={config[field.name] || ''}
                                onChange={handleInputChange}
                                className="flex-1 p-2 border border-gray-200 rounded-lg bg-[#f7f7f9]"
                                placeholder="Paste or upload image URL"
                                disabled={uploading}
                            />
                            {config[field.name] && (
                                <img
                                    src={config[field.name]}
                                    alt={field.name}
                                    className="w-10 h-10 rounded object-cover border ml-2"
                                />
                            )}
                        </div>
                    </div>
                ))}

                {/* Color Pickers */}
                {COLOR_FIELDS.map((field) => (
                    <div key={field.name}>
                        <label className="block text-gray-700 font-medium mb-1">
                            {field.label}
                        </label>
                        <InputColor
                            {...({
                                className: '!w-[calc(100%-16px)] !mx-2 !h-10',
                                initialValue:
                                    config[field.name] || field.default,
                                onChange: handleColorChange(field.name),
                                placement: 'right',
                            } as InputColorProps)}
                        />
                        <input
                            type="text"
                            name={field.name}
                            value={config[field.name] || ''}
                            onChange={handleInputChange}
                            className="w-28 ml-3 p-2 border border-gray-200 rounded-xl bg-[#f7f7f9]"
                        />
                    </div>
                ))}

                {/* Menu Tab Label */}
                <div>
                    <label className="block text-gray-700 font-medium mb-1">
                        Menu Tab Label
                    </label>
                    <input
                        name="menuTabLabel"
                        value={config.menuTabLabel}
                        onChange={handleInputChange}
                        className="w-full p-2 border border-gray-200 rounded-xl bg-[#f7f7f9] focus:outline-none"
                        type="text"
                        placeholder="e.g. Below Employee News"
                    />
                </div>
            </div>
            <div className="pt-3">
                <button
                    type="submit"
                    disabled={isSaving || uploading || !isDirty}
                    className={`w-full bg-[#202020] hover:bg-black text-white py-2 rounded-xl font-semibold transition ${
                        !isDirty || isSaving || uploading
                            ? 'opacity-50 cursor-not-allowed'
                            : ''
                    }`}>
                    {isSaving
                        ? 'Saving...'
                        : uploading
                        ? 'Uploading...'
                        : 'Save Changes'}
                </button>
            </div>
        </form>
    );
};

export default BrandingConfigCard;
